import { NavigationEnd, Router, RouterModule } from '@angular/router';
import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, Output, OnInit, OnDestroy } from '@angular/core';
import { filter, Subscription } from 'rxjs';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { AmplitudeService, AuthenticationService, LicenseService } from '../../services';
import { UserConfigService } from '@core/services';

interface MenuItem {
  icon: string;
  label: string;
  routerLink: string;
  children?: MenuItem[];
  isOpen?: boolean;
}

@Component({
  selector: 'app-sidebar',
  imports: [CommonModule, FontAwesomeModule, RouterModule],
  templateUrl: './sidebar.component.html',
  styleUrl: './sidebar.component.scss',
})
export class SidebarComponent implements OnInit, OnDestroy {
  @Input() isSidebarCollapsed = false;
  /** True while the mobile off-canvas drawer is open. */
  @Input() isMobileOpen = false;
  @Output() sidebarToggle = new EventEmitter<void>();


  menuItems: MenuItem[] = [
    {
      icon: 'fa-solid fa-dollar-sign',
      label: 'Opportunities',
      isOpen: false,
      routerLink: '/dashboard'
    },
    {
      icon: 'fa-solid fa-calendar-days',
      label: 'Past meetings',
      isOpen: false,
      routerLink: '/meetings'
    },
    {
      icon: 'fa-solid fa-question ',
      label: 'Add question',
      isOpen: false,
      routerLink: '/add-question'
    },
    {
      icon: 'fa-solid fa-cloud-arrow-up',
      label: 'Upload',
      routerLink: '/upload'
    }
  ];

  userName = '';
  name = '';
  avatarUrl = '';
  hasConnectedCrm = false;
  isOrgAdmin = false;
  licenseStatusLabel = '';
  licenseStatusLevel: 'warning' | 'danger' | '' = '';
  hasPendingInvite = false;
  private _routerEventsSubscription: Subscription | null = null;
  private _licenseSub?: Subscription;

  constructor(
    private readonly _router: Router,
    private readonly _amplitudeService: AmplitudeService,
    private readonly _authService: AuthenticationService,
    private readonly _userConfigService: UserConfigService,
    private readonly _licenseService: LicenseService,
  ) { }

  ngOnInit(): void {
    this.loadUserInfo();
    this.refreshCrmConnectionState();
    this._refreshLicenseStatus();
    this._checkPendingInvites();

    // Subscribe to license context changes
    this._licenseSub = this._licenseService.context$.subscribe(() => {
      this._refreshLicenseStatus();
    });

    // Set up interval to check for user data updates (for OAuth flow)
    const checkInterval = setInterval(() => {
      const userInfo = this._authService.getUserDetail();
      if (userInfo?.user?.email && this.userName === '-') {
        this.loadUserInfo();
        clearInterval(checkInterval);
      }
    }, 500);

    // Clear interval after 10 seconds to avoid infinite checking
    setTimeout(() => clearInterval(checkInterval), 10000);

    this._routerEventsSubscription = this._router.events
      .pipe(filter((event): event is NavigationEnd => event instanceof NavigationEnd))
      .subscribe(() => {
        this.refreshCrmConnectionState();
      });
  }

  ngOnDestroy(): void {
    this._routerEventsSubscription?.unsubscribe();
    this._licenseSub?.unsubscribe();
  }

  private loadUserInfo(): void {
    const userInfo = this._authService.getUserDetail();
    this.userName = userInfo?.user?.email ?? '-';
    this.name = userInfo?.user?.user_metadata?.full_name || userInfo?.user?.user_metadata?.name || '';

    // Get avatar URL from user_metadata (prioritize avatar_url, fallback to picture)
    this.avatarUrl = userInfo?.user?.user_metadata?.avatar_url || userInfo?.user?.user_metadata?.picture || '';
  }

  goToDashboard(): void {
    this._router.navigate(['/dashboard']);
  }

  logout(): void {
    // Track logout event
    this._amplitudeService.trackEvent('Logout', {
      user_id: this.getUserId(),
      source: 'SidePane'
    });

    this._authService.clearUserDetail();
    this._router.navigate(['/login'])
  }

  openSettings(): void {
    this._amplitudeService.trackEvent('Settings_Open', {
      user_id: this.getUserId(),
      source: 'sidebar_profile_menu'
    });

    this._router.navigate(['/settings']);
  }

  goToCrmSettings(): void {
    this._amplitudeService.trackEvent('CRM_Connect_CTA_Click', {
      user_id: this.getUserId(),
      source: 'sidebar_cta'
    });

    this._router.navigate(['/settings'], { fragment: 'connect-crm-section' }).then(() => {
      this.scrollToElementWithRetry('connect-crm-section');
    });
  }

  toggleSidebar() {
    // Track menu minimise
    this._amplitudeService.trackEvent('Menu_minimise', {
      user_id: this.getUserId(),
      action: 'minimise'
    });

    this.sidebarToggle.emit();
  }

  toggleMenuItem(item: MenuItem) {
    // Updated menu click tracking
    this._amplitudeService.trackEvent('Menu_click', {
      menu_item: item.routerLink.replace('/', ''),
      user_id: this.getUserId()
    });

    if (!this.isSidebarCollapsed && item.children) {
      item.isOpen = !item.isOpen;
    }
  }

  private getUserId(): string {
    const userInfo = this._authService.getUserDetail();
    return userInfo?.user?.id || userInfo?.['id'] || 'anonymous';
  }

  onImageError(): void {
    // If profile image fails to load, clear the URL so fallback icon remains visible
    this.avatarUrl = '';
  }

  private scrollToElementWithRetry(elementId: string, attempts = 8): void {
    const element = document.getElementById(elementId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
      return;
    }

    if (attempts <= 0) {
      return;
    }

    window.setTimeout(() => this.scrollToElementWithRetry(elementId, attempts - 1), 120);
  }

  private async _checkPendingInvites(): Promise<void> {
    try {
      const inviteInfo = await this._licenseService.checkPendingInvite();
      this.hasPendingInvite = inviteInfo.has_invite;
    } catch (err) {
      console.error('Failed to check invites:', err);
    }
  }

  private async refreshCrmConnectionState(): Promise<void> {
    const provider = await this._userConfigService.loadConnectedCrmProvider();
    this.hasConnectedCrm = !!provider;
  }

  private _refreshLicenseStatus(): void {
    this.isOrgAdmin = this._licenseService.isOrgAdmin;
    const state = this._licenseService.effectiveState;
    const days = this._licenseService.daysUntilExpiry;

    if (state === 'expired' || state === 'none') {
      this.licenseStatusLabel = 'License expired';
      this.licenseStatusLevel = 'danger';
    } else if (state === 'in_grace') {
      this.licenseStatusLabel = 'In grace period';
      this.licenseStatusLevel = 'danger';
    } else if (state === 'suspended') {
      this.licenseStatusLabel = 'Account suspended';
      this.licenseStatusLevel = 'danger';
    } else if (state === 'active' && days !== null && days <= 3) {
      this.licenseStatusLabel = days === 0 ? 'Expires today' : days === 1 ? 'Expires tomorrow' : `Expires in ${days} days`;
      this.licenseStatusLevel = 'warning';
    } else {
      this.licenseStatusLabel = '';
      this.licenseStatusLevel = '';
    }
  }
}
